import { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Icons as inline SVGs for simplicity
const HomeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
);

const ChartIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
);

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);

const CalendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const LogoutIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
);

const MenuIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
);

const BookIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
);

export const XIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

export default function Layout() {
    const { user, logout } = useAuth();
    // Use Korean timezone (UTC+9) for today's date
    const kstNow = new Date(Date.now() + (9 * 60 * 60 * 1000));
    const today = kstNow.toISOString().split('T')[0];
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    // Close menu when route changes
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    return (
        <div className="layout">
            {/* Mobile Header with Dropdown Menu */}
            <div className="mobile-layout">
                <header className="mobile-header">
                    <NavLink to="/" className="mobile-logo text-decoration-none">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="32" height="32">
                            <defs>
                                <linearGradient id="logoGradMob" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" style={{ stopColor: '#6366f1' }} />
                                    <stop offset="100%" style={{ stopColor: '#8b5cf6' }} />
                                </linearGradient>
                            </defs>
                            <rect width="100" height="100" rx="20" fill="url(#logoGradMob)" />
                            <path d="M25 65 L40 45 L55 55 L75 30" stroke="white" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="75" cy="30" r="6" fill="white" />
                        </svg>
                        <h1>매매일지</h1>
                    </NavLink>
                    <button className="btn-icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                        {isMobileMenuOpen ? <XIcon /> : <MenuIcon />}
                    </button>
                </header>

                {/* Mobile Dropdown Navigation */}
                {isMobileMenuOpen && (
                    <nav className="mobile-nav-dropdown">
                        <NavLink to="/" className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`} end>
                            <HomeIcon />
                            <span>홈</span>
                        </NavLink>
                        <NavLink to="/positions" className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}>
                            <ChartIcon />
                            <span>포트폴리오</span>
                        </NavLink>
                        <NavLink to="/new" className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}>
                            <PlusIcon />
                            <span>포트폴리오 등록</span>
                        </NavLink>
                        <NavLink to="/reviews" className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}>
                            <BookIcon />
                            <span>복기</span>
                        </NavLink>
                        <NavLink to={`/journal/${today}`} className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}>
                            <CalendarIcon />
                            <span>매매일지</span>
                        </NavLink>
                        <button onClick={logout} className="mobile-nav-link logout">
                            <LogoutIcon />
                            <span>로그아웃</span>
                        </button>
                    </nav>
                )}
            </div>

            {/* Desktop Header Navigation (Replaces Sidebar) */}
            <header className="desktop-header">
                <div className="desktop-header-content">
                    <NavLink to="/" className="desktop-logo text-decoration-none">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                            <defs>
                                <linearGradient id="logoGradDesk" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" style={{ stopColor: '#6366f1' }} />
                                    <stop offset="100%" style={{ stopColor: '#8b5cf6' }} />
                                </linearGradient>
                            </defs>
                            <rect width="100" height="100" rx="20" fill="url(#logoGradDesk)" />
                            <path d="M25 65 L40 45 L55 55 L75 30" stroke="white" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="75" cy="30" r="6" fill="white" />
                        </svg>
                        <h1>매매일지</h1>
                    </NavLink>

                    <nav className="desktop-nav">
                        <NavLink to="/" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} end>
                            <span>홈</span>
                        </NavLink>
                        <NavLink to="/positions" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                            <span>포트폴리오</span>
                        </NavLink>
                        <NavLink to="/new" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                            <span>포트폴리오 등록</span>
                        </NavLink>
                        <NavLink to="/reviews" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                            <span>복기</span>
                        </NavLink>
                        <NavLink to={`/journal/${today}`} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                            <span>매매일지</span>
                        </NavLink>
                    </nav>

                    <div className="desktop-user-actions">
                        <div className="user-info-pill">
                            <div className="user-avatar-sm">
                                {user?.email.charAt(0).toUpperCase()}
                            </div>
                            <span className="user-email-text">{user?.email}</span>
                        </div>
                        <button onClick={logout} className="btn-icon-text">
                            <LogoutIcon />
                        </button>
                    </div>
                </div>
            </header>

            <main className="main-content">
                <Outlet />
            </main>
        </div>
    );
}
